/**
 * Drag&drop de post-its sobre el corcho — Fase 10a.2.
 *
 * Reglas:
 *  - Solo se mueven los post-its con `data-postit-can-move="1"`
 *    (autor o admin+; el markup ya lo decide).
 *  - PointerDown sobre el post-it inicia el "drag candidate". Solo
 *    pasamos a modo drag cuando el cursor se mueve más de
 *    `DRAG_THRESHOLD_PX` desde el origen — por debajo de ese umbral
 *    delegamos en el click (que dispara el expand-to-read habitual).
 *  - Durante el drag, el post-it sigue al cursor (style.left/top en %).
 *  - PointerUp dentro del stage y FUERA de las zonas prohibidas
 *    (`.notams-sheet`, `.notams-hornet`, `.notams-map`) llama a
 *    `onDrop(id, x%, y%)`. Si cae sobre zona prohibida, snap back y
 *    no notifica nada.
 *  - Los clicks sobre los botones internos EDITAR/BORRAR del post-it
 *    no inician drag — el handler los ignora con un `closest()`.
 */

const DRAG_THRESHOLD_PX = 5;

/**
 * Zona válida para soltar un post-it expresada como porcentajes del
 * stage. El resto del `.notams-stage` está cubierto por elementos
 * "baked" en `fondo_notam.png` (marco metálico izquierdo con escudo,
 * cartel NOTAM BOARD superior, lámparas, marco metálico derecho,
 * franja inferior) que NO son nodos del DOM, así que la única forma
 * de detectarlos es por coordenadas.
 *
 * El post-it tiene que caber ENTERO dentro de este rectángulo — el
 * rect bounds del elemento, no solo el cursor.
 */
const WOOD_AREA = { left: 14, right: 95, top: 17, bottom: 93 };

/**
 * Selectores de zonas interactivas-pero-prohibidas: elementos del DOM
 * sobre el corcho donde un post-it no debe acabar.
 *
 *  - `.notams-sheet` — la hoja oficial de NOTAMs (central).
 *  - `.notams-hornet` — foto + Polaroid del Hornet (top-right).
 *
 * El **mapa es decorativo** — los post-its sí pueden colocarse encima
 * (decisión del usuario). El `.notams-howto` queda fuera del rectángulo
 * de madera (vive en la banda metálica), así que ya lo cubre el test
 * de bounds; no necesita estar aquí.
 *
 * Usamos test del centro del post-it contra los rects — el cursor
 * `elementFromPoint` no funciona aquí porque estos elementos tienen
 * `pointer-events: none` para que clicks adyacentes lleguen al corcho.
 */
const FORBIDDEN_SELECTORS = ['.notams-sheet', '.notams-hornet'];

export interface DragDropConfig {
  /** Stage relativo (el `.notams-stage` ya posicionado). */
  stage: HTMLElement;
  /**
   * Callback al hacer drop válido. Recibe el id del post-it y la nueva
   * posición en porcentaje del stage. El caller decide cómo persistir
   * (PATCH al backend, refrescar la lista, etc.).
   */
  onDrop: (postitId: string, xPercent: number, yPercent: number) => void;
}

interface DragState {
  postit: HTMLElement;
  postitId: string;
  pointerId: number;
  /** Bounding del stage en viewport coords (cacheado al inicio). */
  stageRect: DOMRect;
  /** Offset del cursor respecto al top-left del post-it al iniciar. */
  offsetX: number;
  offsetY: number;
  /** Punto donde se inició el pointer (para threshold). */
  startClientX: number;
  startClientY: number;
  /** Estado del propio post-it al iniciar (para snap-back si falla). */
  originalLeft: string;
  originalTop: string;
  originalTransform: string;
  /** ¿Ya pasamos del threshold? */
  isDragging: boolean;
}

let active: DragState | null = null;

/**
 * Activa el drag&drop en el stage. Llama una vez por sesión de mount —
 * no devuelve handle de cleanup porque el módulo es de página completa
 * y el stage se reemplaza al recargar.
 */
export function setupPostitDragDrop(config: DragDropConfig): void {
  config.stage.addEventListener('pointerdown', (ev) => {
    if (ev.button !== 0) return; // Solo botón principal.

    const target = ev.target as HTMLElement;
    // Ignorar clicks sobre los botones internos del post-it.
    if (target.closest('[data-postit-action]')) return;

    const postit = target.closest<HTMLElement>('.notams-postit');
    if (!postit) return;
    if (postit.dataset['postitCanMove'] !== '1') return;

    const postitId = postit.dataset['postitId'];
    if (!postitId) return;

    const stageRect = config.stage.getBoundingClientRect();
    const postitRect = postit.getBoundingClientRect();

    active = {
      postit,
      postitId,
      pointerId: ev.pointerId,
      stageRect,
      offsetX: ev.clientX - postitRect.left,
      offsetY: ev.clientY - postitRect.top,
      startClientX: ev.clientX,
      startClientY: ev.clientY,
      originalLeft: postit.style.left,
      originalTop: postit.style.top,
      originalTransform: postit.style.transform,
      isDragging: false,
    };
  });

  config.stage.addEventListener('pointermove', (ev) => {
    if (!active || ev.pointerId !== active.pointerId) return;

    const dx = ev.clientX - active.startClientX;
    const dy = ev.clientY - active.startClientY;

    // Activar drag solo al superar el umbral — debajo dejamos que se
    // dispare el click normal (expand-to-read).
    if (!active.isDragging) {
      if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
      active.isDragging = true;
      active.postit.classList.add('notams-postit--dragging');
      try {
        active.postit.setPointerCapture(active.pointerId);
      } catch {
        /* algunos navegadores fallan si el target cambió — no crítico */
      }
    }

    // Actualizar posición durante drag.
    const newLeftPx = ev.clientX - active.offsetX - active.stageRect.left;
    const newTopPx = ev.clientY - active.offsetY - active.stageRect.top;
    active.postit.style.left = `${pxToPct(newLeftPx, active.stageRect.width)}%`;
    active.postit.style.top = `${pxToPct(newTopPx, active.stageRect.height)}%`;
  });

  const finishDrag = (ev: PointerEvent): void => {
    if (!active || ev.pointerId !== active.pointerId) return;
    const s = active;
    active = null;
    s.postit.classList.remove('notams-postit--dragging');
    try {
      s.postit.releasePointerCapture(s.pointerId);
    } catch {
      /* ignore */
    }
    if (!s.isDragging) return; // Movimiento sub-threshold: era click.

    if (!isDropAllowed(s.postit, s.stageRect)) {
      // Snap back a la posición de antes del drag.
      s.postit.style.left = s.originalLeft;
      s.postit.style.top = s.originalTop;
      s.postit.style.transform = s.originalTransform;
      return;
    }

    // Drop válido — los porcentajes de style.left/top YA están actualizados
    // por el bucle de pointermove, así que los leemos directamente. Evita
    // tener que descontar el `transform: rotate(...)` del rect.
    const xPct = parseFloat(s.postit.style.left) || 0;
    const yPct = parseFloat(s.postit.style.top) || 0;
    config.onDrop(s.postitId, clamp(xPct, 0, 100), clamp(yPct, 0, 100));
  };

  config.stage.addEventListener('pointerup', finishDrag);
  config.stage.addEventListener('pointercancel', finishDrag);
}

/**
 * ¿El drop final del post-it cae sobre zona permitida?
 *
 * Dos chequeos:
 *  1. El **rect del post-it** debe caber entero dentro del rectángulo
 *     `WOOD_AREA` (el área de madera utilizable del corcho). Esto
 *     descarta marco metálico izquierdo, cartel NOTAM BOARD, lámparas,
 *     marco derecho y franja inferior — todos parte del PNG de fondo.
 *  2. El **centro del post-it** no debe caer sobre ningún elemento
 *     interactivo-prohibido (hoja, foto Hornet, mapa, howto). Usar el
 *     centro permite solapamiento parcial con esos elementos sin
 *     bloquear posiciones legítimas adyacentes.
 *
 * Calculamos el rect "sin transform" interpretando style.left/top en
 * % — `getBoundingClientRect` incluiría el rotate-de-decoración y
 * complica el cálculo de bounds.
 */
function isDropAllowed(postit: HTMLElement, stageRect: DOMRect): boolean {
  const xPct = parseFloat(postit.style.left) || 0;
  const yPct = parseFloat(postit.style.top) || 0;
  // Tamaño del post-it en % del stage (constante mientras drag).
  const r = postit.getBoundingClientRect();
  const wPct = (r.width / stageRect.width) * 100;
  const hPct = (r.height / stageRect.height) * 100;

  // 1) Bounds dentro del rectángulo de madera.
  if (xPct < WOOD_AREA.left) return false;
  if (xPct + wPct > WOOD_AREA.right) return false;
  if (yPct < WOOD_AREA.top) return false;
  if (yPct + hPct > WOOD_AREA.bottom) return false;

  // 2) Centro fuera de los elementos prohibidos del DOM.
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  for (const sel of FORBIDDEN_SELECTORS) {
    const el = document.querySelector(sel);
    if (!el) continue;
    const er = el.getBoundingClientRect();
    if (cx >= er.left && cx <= er.right && cy >= er.top && cy <= er.bottom) {
      return false;
    }
  }
  return true;
}

function pxToPct(px: number, total: number): number {
  if (total <= 0) return 0;
  return (px / total) * 100;
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
